/**
 * Comparação com o 2º turno de 2022 (§8.2 "2022" e "Variação"; §9.10 no `MapTooltip`): fatias de 2022 de um lugar, em
 * ordem de número, e a variação por PARTIDO na ordem do par de 2026 [A, B]. Função pura, sem tema: a sigla de exibição
 * de cada partido é de quem desenha (AC-8.2-04: simétrica e correta mesmo quando o candidato do partido mudou).
 */
import type { Baseline2022 } from '../contracts/static';

/** Uma candidatura de 2022 na linha "2022" (ordem de número). */
export interface Ref2022Item {
  number: '13' | '22';
  name: string;
  party_code: string;
  /** Fatia dos válidos do lugar no 2º turno de 2022. */
  share: number;
}

/** Os votos de 2022 de um lugar (UF: `places[uf]`; município ou cidade: as colunas da linha de base). */
export interface Ref2022Tally {
  valid_votes: number;
  votes: { '13': number; '22': number };
}

export interface Ref2022 {
  items: [Ref2022Item, Ref2022Item];
  /**
   * Variação por partido, na ordem do par de 2026 [A, B]: delta = fatia de 2026 − fatia do mesmo partido em 2022, em
   * fração. null quando um partido do par não disputou o 2º turno de 2022.
   */
  variation: { party: [string, string]; delta: [number, number]; base: [number, number] } | null;
}

/** "2022" e "Variação" de um lugar; null sem linha de base ou sem voto válido em 2022 no lugar. */
export function ref2022Of(
  baseline: Baseline2022 | null | undefined,
  tally: Ref2022Tally | null | undefined,
  pair: readonly [{ party_code: string }, { party_code: string }],
  shares: readonly [number, number],
): Ref2022 | null {
  if (!baseline || !tally || !(tally.valid_votes > 0)) return null;
  const share22 = (n: '13' | '22') => tally.votes[n] / tally.valid_votes;
  const [c0, c1] = [...baseline.candidates].sort((x, y) => Number(x.number) - Number(y.number)) as [
    Baseline2022['candidates'][0],
    Baseline2022['candidates'][1],
  ];
  const item = (c: typeof c0): Ref2022Item => ({
    number: c.number,
    name: c.display_name,
    party_code: c.party_code,
    share: share22(c.number),
  });
  const nA = baseline.party_to_number[pair[0].party_code];
  const nB = baseline.party_to_number[pair[1].party_code];
  let variation: Ref2022['variation'] = null;
  if (nA && nB && nA !== nB) {
    const base: [number, number] = [share22(nA), share22(nB)];
    variation = {
      party: [pair[0].party_code, pair[1].party_code],
      delta: [shares[0] - base[0], shares[1] - base[1]],
      base,
    };
  }
  return { items: [item(c0), item(c1)], variation };
}

/** Os votos de 2022 de um município pela posição dele na geografia; null sem correspondência (−1). */
export function municipalTally2022(baseline: Baseline2022 | null | undefined, idx: number): Ref2022Tally | null {
  const c = baseline?.municipal;
  const valid = c?.valid_votes[idx];
  const a = c?.votes_13[idx];
  const b = c?.votes_22[idx];
  if (valid === undefined || a === undefined || b === undefined || valid < 0 || a < 0 || b < 0) return null;
  return { valid_votes: valid, votes: { '13': a, '22': b } };
}

/** Os votos de 2022 de uma cidade do exterior pela posição dela na geografia; null sem correspondência (−1). */
export function abroadTally2022(baseline: Baseline2022 | null | undefined, idx: number): Ref2022Tally | null {
  const c = baseline?.abroad;
  const valid = c?.valid_votes[idx];
  const a = c?.votes_13[idx];
  const b = c?.votes_22[idx];
  if (valid === undefined || a === undefined || b === undefined || valid < 0 || a < 0 || b < 0) return null;
  return { valid_votes: valid, votes: { '13': a, '22': b } };
}
